import { useEffect, useMemo } from 'react';
import { useStore } from '@/hooks/useStore';
import LateReturnedBikes from './late-returned-bikes';
import BikeStatusCard from './components/bike-status-card/BikeStatusCard';
import BikeIcon from '@/assets/images/dashboard/bike-icon.svg';
import MoneyIcon from '@/assets/images/dashboard/money-icon.svg';
import ClockIcon from '@/assets/images/dashboard/clock.svg';
import { useTranslation } from 'react-i18next';
import { DASHBOARD_LIST_TYPE } from '@/types/enums';
import { observer } from 'mobx-react-lite';
import NeedArrangedOverdue from './need-arranged-overdue';
import { Value } from '@/types/dashboard';
import { formatNumber } from '@/utils/formatNumber';
import LatePayment from './late-payment';

export default observer(function DashboardPage() {
    // hook
    const { t } = useTranslation();

    // store
    const {
        dashboardStore: {
            dashboardListType,
            dashboard,
            getDashboards,
            setObservable,
            clean
        },
        systemSettingStore: { getSystemSetting }
    } = useStore();

    // life cycle
    useEffect(() => {
        getDashboards();
        getSystemSetting();

        return () => {
            clean();
        };
    }, []);

    const vehiclesArranges: Value[] = useMemo((): Value[] => {
        return [
            {
                title: t('dashboard.installed_within_24_hours'),
                value: formatNumber(dashboard?.vehiclesToArrangeIn24h) ?? 0
            },
            {
                title: t('dashboard.installation_expired'),
                value: formatNumber(dashboard?.vehiclesOverdueForArrangement) ?? 0
            }
        ];
    }, [dashboard?.vehiclesToArrangeIn24h, dashboard?.vehiclesOverdueForArrangement]);

    const latePaymentOrders: Value[] = useMemo((): Value[] => {
        return [
            {
                title: t('dashboard.late_payment'),
                value: formatNumber(Number(dashboard?.unpaidOrders)) ?? 0
            }
        ];
    }, [dashboard?.unpaidOrders]);

    const lateReturnedBikes: Value[] = useMemo((): Value[] => {
        return [
            {
                title: t('dashboard.delayed_return'),
                value: formatNumber(dashboard?.lateReturnedBikes) ?? 0
            }
        ];
    }, [dashboard?.lateReturnedBikes]);

    // function
    const handleViewDetail = (type: number) => {
        if (type) {
            setObservable('dashboardListType', type);
        }
    };

    return (
        <div className='flex flex-col gap-6'>
            <div className='grid gap-6 grid-cols-1 xsm:grid-cols-2 lg:grid-cols-3'>
                <BikeStatusCard
                    dashboardListType={DASHBOARD_LIST_TYPE.NeedingArranged24AndOverdue}
                    icon={BikeIcon}
                    backgroundColor='bg-blue'
                    contents={vehiclesArranges}
                    onViewDetail={handleViewDetail}
                />
                <BikeStatusCard
                    dashboardListType={DASHBOARD_LIST_TYPE.LatePayment}
                    icon={MoneyIcon}
                    backgroundColor='bg-orange'
                    contents={latePaymentOrders}
                    onViewDetail={handleViewDetail}
                />
                <BikeStatusCard
                    dashboardListType={DASHBOARD_LIST_TYPE.LateReturn}
                    icon={ClockIcon}
                    imgClassName='w-[35px] h-[35px]'
                    backgroundColor='bg-green'
                    contents={lateReturnedBikes}
                    onViewDetail={handleViewDetail}
                />
            </div>

            {dashboardListType === DASHBOARD_LIST_TYPE.NeedingArranged24AndOverdue && (
                <NeedArrangedOverdue />
            )}
            {dashboardListType === DASHBOARD_LIST_TYPE.LatePayment && (
                <LatePayment />
            )}
            {dashboardListType === DASHBOARD_LIST_TYPE.LateReturn && (
                <LateReturnedBikes />
            )}
        </div>
    );
});
